import React, { Component } from "react";
import {
  Select,
  FormControl,
  FormControlLabel,
  MenuItem,
  InputLabel,
  FormHelperText,
  TextField,
  Chip,
  ListItemIcon,
  ListItemText,
  Button,
  ThemeProvider,
  Checkbox,
  Box,
  Tooltip,
} from "@mui/material";
import { baseTheme } from "mui-theme";

import { format } from "date-fns";
import { toast } from "react-toastify";
import { diff } from "deep-object-diff";
import { validateRPFormula } from "App/utils/formulaValidator";
import { Formik, FormikActions } from "formik";
import * as Yup from "yup";

import { YieldApi } from "api/yield";
import Popup from "reusable-components/popup/popup";
import { ACTIONS, hasPermission } from "App/common/permissionHandler";
import { ConfigApi } from "api/config";

interface IEditRatePlanPanelProps {
  isNew: boolean;
  ratePlan: RatePlan;
  user: any;
  hotel: any;
  onclose: () => void;
  onUpdate: (rp: string) => void;
  unEditableRP: boolean;
  counter: number;
}

interface State {
  isCreateRpDialogOpened: boolean;
  rateplanData: RatePlan;
  rtMinMaxValue: Record<string, any>;
}

export interface RatePlan {
  id: string;
  shortDescription: string;
  longDescription: string;
  modeOfSale: string;
  isManual: boolean;
  formula?: string;
  defaultRate?: number; // Set Rate
  applies: string[];
  hotelId?: string;
  autoCloseOut?: {
    openStart: string;
    openEnd: string;
  };
  DOWVariation: {
    enabled: boolean;
    setRate: {
      [roomTypeId: string]: {
        monday: string;
        tuesday: string;
        wednesday: string;
        thursday: string;
        friday: string;
        saturday: string;
        sunday: string;
      };
    };
  };
  DOWStopSell?: {
    enabled: boolean;
    stop_sell: {
      [roomTypeId: string]: {
        monday: boolean;
        tuesday: boolean;
        wednesday: boolean;
        thursday: boolean;
        friday: boolean;
        saturday: boolean;
        sunday: boolean;
      };
    };
  };
}

class EditRatePlanPanel extends Component<IEditRatePlanPanelProps, State> {
  private submitForm?: () => void;
  readonly state: State = {
    rateplanData: {
      id: "",
      longDescription: "",
      shortDescription: "",
      modeOfSale: "",
      isManual: false,
      applies: [],
      DOWVariation: {
        enabled: false,
        setRate: {},
      },
      DOWStopSell: {
        enabled: false,
        stop_sell: {},
      },
    },
    rtMinMaxValue: {},
    isCreateRpDialogOpened: false,
  };

  constructor(props: any) {
    super(props);
    this.state.rateplanData = this.props.ratePlan;
  }

  closeCreateRpDialog = () => {
    this.setState({
      isCreateRpDialogOpened: false,
    });
  };

  // Check if the rate is within min-max range of room types
  checkRateWithinMinMax = (roomTypes: string[], rate: number): boolean => {
    const roomtypes = this.props.hotel.getRoomTypes();
    let allValid = true;

    if (roomTypes.length === 0) return true;

    for (const roomTypeId of roomTypes) {
      let foundRoomType = false;
      for (const rt of roomtypes) {
        if (roomTypeId === rt.id) {
          foundRoomType = true;
          const minRate = Number(rt.minRate);
          const maxRate = Number(rt.maxRate);

          if (!(minRate <= rate && rate <= maxRate)) {
            allValid = false;
            break;
          }
        }
      }

      if (!foundRoomType || !allValid) {
        return false;
      }
    }

    return true;
  };

  // Create a new rate plan
  createRatePlan = (values: RatePlan, actions: FormikActions<RatePlan>) => {
    const hotel = this.props.hotel;
    const rp = { ...values }; // Copy values to avoid reference issues

    // Handle exclusions based on manual rate vs formula
    if (rp.isManual) {
      delete rp.formula;
    } else {
      delete rp.defaultRate;
    }

    if (!this.props.isNew) {
      // Compare with original rate plan data to detect changes
      const difs = diff(rp, JSON.parse(JSON.stringify(hotel.ratePlans[rp.id])));

      // If no changes, notify user and close panel
      if (Object.keys(difs).length === 0) {
        toast.warn("No change detected");
        this.props.onclose();
        actions.setSubmitting(false);
        return;
      }
    }

    // Add hotel ID
    rp.hotelId = hotel.hotelId;

    // Open confirmation dialog (don't close parent panel yet)
    this.setState({
      rateplanData: rp,
      isCreateRpDialogOpened: true,
    });

    actions.setSubmitting(false);
  };

  // Call API to write rate plan
  callWriteRatePlan = async (rp: any) => {
    const actionName = this.props.isNew ? "creation" : "modification";
    await toast.promise(ConfigApi.writeRateplan(rp), {
      pending: "Rate Plan " + actionName + " in progress...",
      success: "Rate Plan " + actionName + " completed",
      error: "Rate Plan " + actionName + " failed",
    });
    this.props.onUpdate(rp);
  };

  render() {
    const rtoptions = this.props.hotel
      .getRoomTypes()
      .filter((rt: any) => !rt.deleted)
      .map((rt: { shortDescription: string; id: string }) => {
        return { label: rt.shortDescription, value: rt.id };
      });

    const rpData = this.state.rateplanData;
    const isAdminUser = hasPermission(ACTIONS.EDIT_ROOM);

    const defaultRateTest = Yup.number()
      .required("Mandatory field")
      .min(0)
      .label("Rate")
      .test(
        "is-rate-within-min-max",
        "Rate lower than the room type's minimum rate or greater than the room type's maximum rate not allowed.",
        (value) =>
          !value ||
          this.checkRateWithinMinMax(
            this.state.rateplanData.applies,
            Number(value),
          ),
      );

    const DOWFormulaTest = Yup.lazy(() => {
      if (this.state.rateplanData.isManual) {
        return defaultRateTest;
      }

      return Yup.string()
        .test(
          "is-valid-formula",
          "Invalid formula",
          (value) => !value || validateRPFormula(value),
        )
        .required("Mandatory field")
        .label("Formula");
    });

    const createDOWValidationSchema = (applies: string[]) => {
      const setRateSchema: Record<string, any> = {};

      applies.forEach((roomTypeId) => {
        setRateSchema[roomTypeId] = Yup.object().shape({
          monday: DOWFormulaTest,
          tuesday: DOWFormulaTest,
          wednesday: DOWFormulaTest,
          thursday: DOWFormulaTest,
          friday: DOWFormulaTest,
          saturday: DOWFormulaTest,
          sunday: DOWFormulaTest,
        });
      });

      return Yup.object().shape({
        enabled: Yup.boolean(),
        setRate: Yup.lazy((value, options) => {
          const { parent } = options;
          return parent.enabled
            ? Yup.object().shape(setRateSchema)
            : Yup.object().nullable();
        }),
      });
    };

    // Create validation schema for DOWStopSell (booleans per day per room type)
    const createDOWStopSellValidationSchema = (applies: string[]) => {
      const stopSellSchema: Record<string, any> = {};

      applies.forEach((roomTypeId) => {
        stopSellSchema[roomTypeId] = Yup.object().shape({
          monday: Yup.boolean(),
          tuesday: Yup.boolean(),
          wednesday: Yup.boolean(),
          thursday: Yup.boolean(),
          friday: Yup.boolean(),
          saturday: Yup.boolean(),
          sunday: Yup.boolean(),
        });
      });

      return Yup.object().shape({
        enabled: Yup.boolean(),
        stop_sell: Yup.lazy((value, options) => {
          const { parent } = options;
          return parent.enabled
            ? Yup.object().shape(stopSellSchema)
            : Yup.object().nullable();
        }),
      });
    };

    // Create validation schema for the rate plan form
    const validationSchema = Yup.object().shape({
      shortDescription: Yup.string()
        .label("Rate Plan Name")
        .required("Mandatory field"),
      id: Yup.string()
        .label("ID")
        .required("Mandatory field")
        .test("is-id-unique", "Rate Plan ID already exists", (value) => {
          if (this.props.isNew) {
            const ratePlan = this.props.hotel.ratePlans[value];
            return !ratePlan || ratePlan.deleted;
          }
          // For existing rate plans, we don't check uniqueness
          return true;
        }),
      modeOfSale: Yup.string()
        .label("Mode of sale")
        .required("Mandatory field"),
      defaultRate: Yup.number()
        .label("Set Rate")
        .when("isManual", {
          is: true,
          then: (schema) =>
            schema.when("DOWVariation.enabled", {
              is: false,
              then: () => defaultRateTest,
              otherwise: () => schema.nullable(), // because we then switch to formula
            }),
          otherwise: () => Yup.number().nullable(),
        }),
      formula: Yup.string()
        .label("formula")
        .when("DOWVariation.enabled", {
          is: false,
          then: (schema) =>
            schema.when("isManual", {
              is: false,
              then: () =>
                Yup.string()
                  .test(
                    "is-valid-formula",
                    "Invalid formula",
                    (value) => !value || validateRPFormula(value),
                  )
                  .required("Mandatory field"),
              otherwise: () => schema.nullable(), // because we then switch to defaultRate
            }),
          otherwise: () => Yup.string().nullable(),
        }),
      DOWVariation: Yup.lazy(() => {
        return createDOWValidationSchema(this.state.rateplanData.applies || []);
      }),
      DOWStopSell: Yup.lazy(() => {
        return createDOWStopSellValidationSchema(
          this.state.rateplanData.applies || [],
        );
      }),
    });

    return (
      <>
        <Formik<RatePlan>
          initialValues={{
            id: rpData.id || "",
            shortDescription: rpData.shortDescription || "",
            longDescription: rpData.longDescription || "",
            modeOfSale: rpData.modeOfSale || "",
            isManual: rpData.isManual || false,
            formula: rpData.formula || "",
            defaultRate: rpData.defaultRate || undefined,
            applies: rpData.applies || [],
            autoCloseOut: rpData.autoCloseOut || undefined,
            DOWVariation: {
              enabled: rpData.DOWVariation.enabled || false,
              setRate: rpData.DOWVariation.setRate || {},
            },
            DOWStopSell: {
              enabled: rpData.DOWStopSell?.enabled || false,
              stop_sell: rpData.DOWStopSell?.stop_sell || {},
            },
          }}
          validationSchema={validationSchema}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={(values, actions) => {
            this.createRatePlan(values, actions);
          }}
          validate={(values) => {
            let errors = {};
            validationSchema
              .validate(values, { abortEarly: false })
              .catch((validationErrors) => {
                console.log("Validation failed:", validationErrors.inner);
              });
            return errors;
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            submitForm,
          }) => {
            this.submitForm = submitForm;
            const hasHighPermission =
              this.props.hotel.info?.level == "Pro"
                ? hasPermission(ACTIONS.MANAGE_PRO_HOTEL)
                : hasPermission(ACTIONS.ADD_ROOM);
            const rawAutoCO: any = (this.state.rateplanData as any)
              .autoCloseOut;
            const isTempRpReadOnly = Array.isArray(rawAutoCO);
            const readOnlyTooltip = hasHighPermission
              ? "Please use the new Revanista UI to modify temporary rate plans"
              : "Contact Systems team to have this modified";
            const lacksIdPermission =
              !this.props.isNew &&
              (this.props.hotel.info?.level == "Pro"
                ? !hasPermission(ACTIONS.MANAGE_PRO_HOTEL)
                : !hasPermission(ACTIONS.ADD_ROOM));
            const idTooltipText = isTempRpReadOnly
              ? readOnlyTooltip
              : lacksIdPermission
                ? "Contact Systems team to have this modified"
                : "";
            const canCreateTempNotice =
              hasPermission(ACTIONS.ADD_ROOM) ||
              hasPermission(ACTIONS.MANAGE_PRO_HOTEL);
            const formContent = (
              <form onSubmit={handleSubmit}>
                <Box sx={{ "& .MuiTextField-root": { m: 1, width: "25ch" } }}>
                  <div>
                    {this.props.isNew && (
                      <div
                        style={{ marginLeft: 4, fontSize: 18, marginBottom: 8 }}
                      >
                        <b>New Rate Plan</b>
                      </div>
                    )}
                    {!this.props.isNew && (
                      <div
                        style={{ marginLeft: 4, fontSize: 18, marginBottom: 8 }}
                      >
                        <b>Edit Rate Plan:</b>{" "}
                        <span style={{ color: "#d93025" }}>
                          {rpData.shortDescription}
                        </span>{" "}
                        ({rpData.id})
                      </div>
                    )}
                    <TextField
                      id="outlined-basic"
                      label="Rate Plan Name"
                      variant="outlined"
                      name="shortDescription"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.shortDescription}
                      disabled={!isAdminUser}
                      error={
                        touched.shortDescription &&
                        Boolean(errors.shortDescription)
                      }
                      helperText={
                        touched.shortDescription &&
                        (errors.shortDescription as string)
                      }
                    />
                    <Tooltip
                      title={idTooltipText}
                      disableHoverListener={!idTooltipText}
                    >
                      <span>
                        <TextField
                          id="outlined-basic"
                          label="ID"
                          variant="outlined"
                          name="id"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.id}
                          disabled={
                            !this.props.isNew &&
                            (this.props.hotel.info?.level == "Pro"
                              ? !hasPermission(ACTIONS.MANAGE_PRO_HOTEL)
                              : !hasPermission(ACTIONS.ADD_ROOM))
                          }
                          error={touched.id && Boolean(errors.id)}
                          helperText={touched.id && (errors.id as string)}
                        />
                      </span>
                    </Tooltip>
                  </div>
                  <div>
                    <FormControl sx={{ m: 1, width: 250 }}>
                      <InputLabel id="mutiple-select-label">
                        Room Type
                      </InputLabel>
                      <Select
                        labelId="mutiple-select-label"
                        label="Room Type"
                        name="applies"
                        multiple
                        value={values.applies}
                        disabled={!isAdminUser}
                        onChange={(e) => {
                          const selectedValues = e.target.value as string[];
                          if (selectedValues.includes("all")) {
                            if (selectedValues.length === 1) {
                              const allRoomTypes = rtoptions.map(
                                (option: any) => option.value,
                              );
                              setFieldValue("applies", allRoomTypes);

                              // Update DOWVariation structure if enabled
                              if (values.DOWVariation.enabled) {
                                const newSetRate = {
                                  ...values.DOWVariation.setRate,
                                };
                                allRoomTypes.forEach((roomTypeId: any) => {
                                  if (!newSetRate[roomTypeId]) {
                                    newSetRate[roomTypeId] = {
                                      monday: "",
                                      tuesday: "",
                                      wednesday: "",
                                      thursday: "",
                                      friday: "",
                                      saturday: "",
                                      sunday: "",
                                    };
                                  }
                                });
                                setFieldValue(
                                  "DOWVariation.setRate",
                                  newSetRate,
                                );
                              }

                              // Update DOWStopSell structure if enabled
                              if (values.DOWStopSell?.enabled) {
                                const newStopSell = {
                                  ...(values.DOWStopSell.stop_sell || {}),
                                } as any;
                                allRoomTypes.forEach((roomTypeId: any) => {
                                  if (!newStopSell[roomTypeId]) {
                                    newStopSell[roomTypeId] = {
                                      monday: false,
                                      tuesday: false,
                                      wednesday: false,
                                      thursday: false,
                                      friday: false,
                                      saturday: false,
                                      sunday: false,
                                    };
                                  }
                                });
                                setFieldValue(
                                  "DOWStopSell.stop_sell",
                                  newStopSell,
                                );
                              }

                              this.setState({
                                rateplanData: {
                                  ...this.state.rateplanData,
                                  applies: allRoomTypes,
                                },
                              });
                            } else {
                              setFieldValue("applies", []);
                              if (values.DOWVariation.enabled) {
                                setFieldValue("DOWVariation.setRate", {});
                              }
                              if (values.DOWStopSell?.enabled) {
                                setFieldValue("DOWStopSell.stop_sell", {});
                              }
                              this.setState({
                                rateplanData: {
                                  ...this.state.rateplanData,
                                  applies: [],
                                },
                              });
                            }
                          } else {
                            setFieldValue("applies", selectedValues);

                            // Update DOWVariation structure if enabled
                            if (values.DOWVariation.enabled) {
                              const newSetRate = {
                                ...values.DOWVariation.setRate,
                              };
                              selectedValues.forEach((roomTypeId) => {
                                if (!newSetRate[roomTypeId]) {
                                  newSetRate[roomTypeId] = {
                                    monday: "",
                                    tuesday: "",
                                    wednesday: "",
                                    thursday: "",
                                    friday: "",
                                    saturday: "",
                                    sunday: "",
                                  };
                                }
                              });
                              setFieldValue("DOWVariation.setRate", newSetRate);
                            }

                            // Update DOWStopSell structure if enabled
                            if (values.DOWStopSell?.enabled) {
                              const newStopSell = {
                                ...(values.DOWStopSell.stop_sell || {}),
                              } as any;
                              selectedValues.forEach((roomTypeId) => {
                                if (!newStopSell[roomTypeId]) {
                                  newStopSell[roomTypeId] = {
                                    monday: false,
                                    tuesday: false,
                                    wednesday: false,
                                    thursday: false,
                                    friday: false,
                                    saturday: false,
                                    sunday: false,
                                  };
                                }
                              });
                              setFieldValue(
                                "DOWStopSell.stop_sell",
                                newStopSell,
                              );
                            }

                            this.setState({
                              rateplanData: {
                                ...this.state.rateplanData,
                                applies: selectedValues,
                              },
                            });
                          }
                        }}
                        renderValue={() => (
                          <div style={{ display: "flex", flexWrap: "wrap" }}>
                            {(values.applies as string[])
                              .filter((value: string) => value !== "all")
                              .map((value: string) => {
                                const chip = rtoptions.find(
                                  (item: any) => item.value === value,
                                );
                                const label = chip ? chip.label : "";
                                if (label) {
                                  return (
                                    <Chip
                                      key={value}
                                      label={label}
                                      style={{ margin: "2px" }}
                                    />
                                  );
                                }
                                return null;
                              })}
                          </div>
                        )}
                      >
                        <MenuItem value="all">
                          <ListItemIcon>
                            <Checkbox
                              checked={
                                values.applies.length === rtoptions.length
                              }
                              indeterminate={
                                values.applies.length > 0 &&
                                values.applies.length < rtoptions.length
                              }
                            />
                          </ListItemIcon>
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {rtoptions.map((option: any) => (
                          <MenuItem key={option.value} value={option.value}>
                            <ListItemIcon>
                              <Checkbox
                                checked={
                                  values.applies.indexOf(option.value) > -1
                                }
                              />
                            </ListItemIcon>
                            <ListItemText primary={option.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl sx={{ m: 1, width: 250 }}>
                      <InputLabel>Mode of sale</InputLabel>
                      <Select
                        fullWidth
                        label="Mode of sale"
                        variant="outlined"
                        name="modeOfSale"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.modeOfSale}
                        disabled={!isAdminUser}
                        error={touched.modeOfSale && Boolean(errors.modeOfSale)}
                      >
                        <MenuItem value="OTA">OTA</MenuItem>
                        <MenuItem value="OWS">OWS</MenuItem>
                        <MenuItem value="GDS">GDS</MenuItem>
                        {this.props.hotel.meta.hasRewards === true && (
                          <MenuItem value="Rewards">Rewards</MenuItem>
                        )}
                      </Select>
                      {touched.modeOfSale && errors.modeOfSale && (
                        <FormHelperText error>
                          {errors.modeOfSale as string}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </div>

                  <div>
                    {!values.DOWVariation.enabled && !values.isManual && (
                      <TextField
                        name="formula"
                        value={values.formula}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!isAdminUser}
                        label="Formula"
                        variant="outlined"
                        error={touched.formula && Boolean(errors.formula)}
                        helperText={
                          touched.formula && (errors.formula as string)
                        }
                      />
                    )}
                    {!values.DOWVariation.enabled && values.isManual && (
                      <TextField
                        type="number"
                        name="defaultRate"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!isAdminUser}
                        value={values.defaultRate}
                        label="Set Rate"
                        variant="outlined"
                        error={
                          touched.defaultRate && Boolean(errors.defaultRate)
                        }
                        helperText={
                          touched.defaultRate && (errors.defaultRate as string)
                        }
                      />
                    )}
                  </div>

                  <div>
                    <FormControlLabel
                      sx={{
                        alignItems: "center",
                        height: "100%",
                        margin: "15px 0",
                      }}
                      control={
                        <Checkbox
                          checked={values.isManual}
                          onChange={(e) => {
                            setFieldValue("isManual", e.target.checked);
                            setFieldValue(
                              "DOWVariation.isManual",
                              e.target.checked,
                            );
                            this.setState({
                              rateplanData: {
                                ...this.state.rateplanData,
                                isManual: e.target.checked,
                              },
                            });

                            // When switching, clear the other value
                            if (e.target.checked) {
                              setFieldValue("formula", "");
                            } else {
                              setFieldValue("defaultRate", "");
                            }
                          }}
                          disabled={!isAdminUser}
                          name="isManual"
                          id="set-rate"
                          color="primary"
                        />
                      }
                      label="Set rate"
                    />

                    <FormControlLabel
                      sx={{
                        alignItems: "center",
                        height: "100%",
                        margin: "15px 0",
                      }}
                      control={
                        <Checkbox
                          checked={values.DOWVariation.enabled}
                          onChange={(e) => {
                            setFieldValue(
                              "DOWVariation.enabled",
                              e.target.checked,
                            );

                            // Initialize DOWVariation structure for each room type when enabled
                            if (e.target.checked) {
                              setFieldValue("formula", "");
                              const newSetRate = {
                                ...values.DOWVariation.setRate,
                              };
                              values.applies.forEach((roomTypeId) => {
                                if (!newSetRate[roomTypeId]) {
                                  newSetRate[roomTypeId] = {
                                    monday: "",
                                    tuesday: "",
                                    wednesday: "",
                                    thursday: "",
                                    friday: "",
                                    saturday: "",
                                    sunday: "",
                                  };
                                }
                              });
                              setFieldValue("DOWVariation.setRate", newSetRate);
                            }
                          }}
                          disabled={!isAdminUser}
                          name="DOWVariation.enabled"
                          color="primary"
                          id="dow-variation"
                        />
                      }
                      label="Day of week variation"
                    />
                  </div>

                  <div>
                    <FormControlLabel
                      sx={{
                        alignItems: "center",
                        height: "100%",
                        margin: "15px 0",
                      }}
                      control={
                        <Checkbox
                          checked={values.DOWStopSell?.enabled || false}
                          onChange={(e) => {
                            setFieldValue(
                              "DOWStopSell.enabled",
                              e.target.checked,
                            );

                            if (e.target.checked) {
                              const newStopSell = {
                                ...(values.DOWStopSell?.stop_sell || {}),
                              } as any;
                              values.applies.forEach((roomTypeId) => {
                                if (!newStopSell[roomTypeId]) {
                                  newStopSell[roomTypeId] = {
                                    monday: false,
                                    tuesday: false,
                                    wednesday: false,
                                    thursday: false,
                                    friday: false,
                                    saturday: false,
                                    sunday: false,
                                  };
                                }
                              });
                              setFieldValue(
                                "DOWStopSell.stop_sell",
                                newStopSell,
                              );
                            }
                          }}
                          disabled={!isAdminUser}
                          name="DOWStopSell.enabled"
                          color="primary"
                          id="dow-stopsell"
                        />
                      }
                      label="Day of week stop-sell"
                    />
                  </div>

                  {/* for each room type in values display a dow variation */}
                  {values.DOWVariation.enabled &&
                    values.applies.length > 0 &&
                    values.applies.map((roomTypeId: string) => (
                      <div>
                        <span>
                          Day of Week Variation for{" "}
                          {rtoptions.find(
                            (item: any) => item.value === roomTypeId,
                          ).label || roomTypeId}
                        </span>
                        <br />
                        <TextField
                          type={values.isManual ? "number" : "text"}
                          style={{ marginBottom: 8, minWidth: 60 }}
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isAdminUser}
                          name={`DOWVariation.setRate.${roomTypeId}.monday`}
                          label="Mon"
                          variant="outlined"
                          value={
                            values.DOWVariation.setRate?.[roomTypeId]?.monday
                          }
                          error={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.monday &&
                            Boolean(
                              errors.DOWVariation?.setRate?.[roomTypeId]
                                ?.monday,
                            )
                          }
                          helperText={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.monday &&
                            (errors.DOWVariation?.setRate?.[roomTypeId]
                              ?.monday as string)
                          }
                        />
                        <TextField
                          type={values.isManual ? "number" : "text"}
                          style={{ marginBottom: 8, minWidth: 60 }}
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isAdminUser}
                          name={`DOWVariation.setRate.${roomTypeId}.tuesday`}
                          label="Tue"
                          variant="outlined"
                          value={
                            values.DOWVariation.setRate?.[roomTypeId]?.tuesday
                          }
                          error={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.tuesday &&
                            Boolean(
                              errors.DOWVariation?.setRate?.[roomTypeId]
                                ?.tuesday,
                            )
                          }
                          helperText={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.tuesday &&
                            (errors.DOWVariation?.setRate?.[roomTypeId]
                              ?.tuesday as string)
                          }
                        />
                        <TextField
                          type={values.isManual ? "number" : "text"}
                          style={{ marginBottom: 8, minWidth: 60 }}
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isAdminUser}
                          name={`DOWVariation.setRate.${roomTypeId}.wednesday`}
                          label="Wed"
                          variant="outlined"
                          value={
                            values.DOWVariation.setRate?.[roomTypeId]?.wednesday
                          }
                          error={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.wednesday &&
                            Boolean(
                              errors.DOWVariation?.setRate?.[roomTypeId]
                                ?.wednesday,
                            )
                          }
                          helperText={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.wednesday &&
                            (errors.DOWVariation?.setRate?.[roomTypeId]
                              ?.wednesday as string)
                          }
                        />
                        <TextField
                          type={values.isManual ? "number" : "text"}
                          style={{ marginBottom: 8, minWidth: 60 }}
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isAdminUser}
                          name={`DOWVariation.setRate.${roomTypeId}.thursday`}
                          label="Thu"
                          variant="outlined"
                          value={
                            values.DOWVariation.setRate?.[roomTypeId]?.thursday
                          }
                          error={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.thursday &&
                            Boolean(
                              errors.DOWVariation?.setRate?.[roomTypeId]
                                ?.thursday,
                            )
                          }
                          helperText={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.thursday &&
                            (errors.DOWVariation?.setRate?.[roomTypeId]
                              ?.thursday as string)
                          }
                        />
                        <TextField
                          type={values.isManual ? "number" : "text"}
                          style={{ marginBottom: 8, minWidth: 60 }}
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isAdminUser}
                          name={`DOWVariation.setRate.${roomTypeId}.friday`}
                          label="Fri"
                          variant="outlined"
                          value={
                            values.DOWVariation.setRate?.[roomTypeId]?.friday
                          }
                          error={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.friday &&
                            Boolean(
                              errors.DOWVariation?.setRate?.[roomTypeId]
                                ?.friday,
                            )
                          }
                          helperText={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.friday &&
                            (errors.DOWVariation?.setRate?.[roomTypeId]
                              ?.friday as string)
                          }
                        />
                        <TextField
                          type={values.isManual ? "number" : "text"}
                          style={{ marginBottom: 8, minWidth: 60 }}
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isAdminUser}
                          name={`DOWVariation.setRate.${roomTypeId}.saturday`}
                          label="Sat"
                          variant="outlined"
                          value={
                            values.DOWVariation.setRate?.[roomTypeId]?.saturday
                          }
                          error={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.saturday &&
                            Boolean(
                              errors.DOWVariation?.setRate?.[roomTypeId]
                                ?.saturday,
                            )
                          }
                          helperText={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.saturday &&
                            (errors.DOWVariation?.setRate?.[roomTypeId]
                              ?.saturday as string)
                          }
                        />
                        <TextField
                          type={values.isManual ? "number" : "text"}
                          style={{ marginBottom: 8, minWidth: 60 }}
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isAdminUser}
                          name={`DOWVariation.setRate.${roomTypeId}.sunday`}
                          label="Sun"
                          variant="outlined"
                          value={
                            values.DOWVariation.setRate?.[roomTypeId]?.sunday
                          }
                          error={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.sunday &&
                            Boolean(
                              errors.DOWVariation?.setRate?.[roomTypeId]
                                ?.sunday,
                            )
                          }
                          helperText={
                            touched.DOWVariation?.setRate?.[roomTypeId]
                              ?.sunday &&
                            (errors.DOWVariation?.setRate?.[roomTypeId]
                              ?.sunday as string)
                          }
                        />
                        <br />
                        <br />
                      </div>
                    ))}

                  {/* DOW Stop Sell per room type */}
                  {values.DOWStopSell?.enabled &&
                    values.applies.length > 0 &&
                    values.applies.map((roomTypeId: string) => (
                      <div>
                        <span>
                          Day of Week Stop-Sell for{" "}
                          {rtoptions.find(
                            (item: any) => item.value === roomTypeId,
                          ).label || roomTypeId}
                        </span>
                        <br />
                        {(
                          [
                            "monday",
                            "tuesday",
                            "wednesday",
                            "thursday",
                            "friday",
                            "saturday",
                            "sunday",
                          ] as const
                        ).map((day) => (
                          <FormControlLabel
                            key={`${roomTypeId}-${day}`}
                            control={
                              <Checkbox
                                checked={Boolean(
                                  values.DOWStopSell?.stop_sell?.[roomTypeId]?.[
                                    day
                                  ],
                                )}
                                onChange={(e) => {
                                  setFieldValue(
                                    `DOWStopSell.stop_sell.${roomTypeId}.${day}`,
                                    e.target.checked,
                                  );
                                }}
                                disabled={!isAdminUser}
                                name={`DOWStopSell.stop_sell.${roomTypeId}.${day}`}
                                color="primary"
                              />
                            }
                            label={`${day.charAt(0).toUpperCase()}${day.slice(1, 3)}`}
                          />
                        ))}
                        <br />
                        <br />
                      </div>
                    ))}
                  <div>
                    <FormControlLabel
                      sx={{
                        alignitems: "center",
                        height: "100%",
                        margin: "15px 0",
                      }}
                      control={
                        <Checkbox
                          checked={values.autoCloseOut ? true : false}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) {
                              // Initialize with default date range when enabled
                              setFieldValue("autoCloseOut", {
                                openStart: format(new Date(), "yyyy-MM-dd"),
                                openEnd: format(new Date(), "yyyy-MM-dd"),
                              });
                            } else {
                              setFieldValue("autoCloseOut", false);
                            }
                          }}
                          disabled
                          name="autoCloseOut"
                          color="primary"
                          id="temporary"
                        />
                      }
                      label="Temporary"
                    />
                    {canCreateTempNotice && (
                      <FormHelperText sx={{ ml: 1 }}>
                        Temporary settings are editable only in the new
                        Revanista UI.
                      </FormHelperText>
                    )}
                    {!canCreateTempNotice && (
                      <FormHelperText sx={{ ml: 1 }}>
                        Please contact us if you require any changes to be made
                        for temporary rates.
                      </FormHelperText>
                    )}
                    {values.autoCloseOut && (
                      <div>
                        <TextField
                          value={values.autoCloseOut.openStart}
                          variant="outlined"
                          id="outlined-basic"
                          label="Start"
                          type="date"
                          onChange={(e) => {
                            setFieldValue("autoCloseOut", {
                              ...values.autoCloseOut,
                              openStart: e.target.value,
                            });
                          }}
                          disabled
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            min: this.props.isNew
                              ? new Date().toISOString().slice(0, 10)
                              : rpData.autoCloseOut?.openStart || undefined,
                          }}
                        />
                        <TextField
                          value={values.autoCloseOut.openEnd}
                          variant="outlined"
                          id="outlined-basic"
                          label="End"
                          type="date"
                          onChange={(e) => {
                            setFieldValue("autoCloseOut", {
                              ...values.autoCloseOut,
                              openEnd: e.target.value,
                            });
                          }}
                          disabled
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            min: this.props.isNew
                              ? values.autoCloseOut.openStart
                              : rpData.autoCloseOut?.openEnd &&
                                  rpData.autoCloseOut?.openEnd >
                                    values.autoCloseOut.openStart
                                ? rpData.autoCloseOut?.openEnd
                                : values.autoCloseOut.openStart,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </Box>
              </form>
            );

            return (
              <Popup
                isPopupOpen={true}
                closeOnOutsideClick={true}
                title={`${this.props.isNew ? "Create" : "Edit"} Rate Plan`}
                content={[formContent]}
                action={() => submitForm()}
                actionName="Save"
                cancelAction={this.props.onclose}
                cancelName={isAdminUser ? "Cancel" : "Close"}
                actionDisabled={isSubmitting}
                hideAction={!isAdminUser}
                maxWidth="md"
              />
            );
          }}
        </Formik>

        <Popup
          isPopupOpen={this.state.isCreateRpDialogOpened}
          closeOnOutsideClick={true}
          title={`${this.props.isNew ? "Create" : "Edit"} ${
            this.state.rateplanData.shortDescription
          }`}
          content={[
            `Upon ${
              this.props.isNew ? "creation" : "modification"
            } of a rate plan, base rate for the next 2 years will be set to unpublished. Please review the adjustments on the yield sheet and proceed to publish them to the channel manager.`,
          ]}
          action={() => {
            this.callWriteRatePlan(this.state.rateplanData);
            this.closeCreateRpDialog();
            this.props.onclose();
          }}
          actionName={`${this.props.isNew ? "Create" : "Edit"} Rate Plan`}
          cancelAction={this.closeCreateRpDialog}
          cancelName="Cancel"
        />
      </>
    );
  }
}

export default EditRatePlanPanel;
